import React, { useEffect, useMemo, useState } from "react";
import { TextHeader } from "components/Texts";
import { useDispatch, useSelector } from "react-redux";

import Section from "components/Section";
import Button from "components/Buttons";
import Toast from "components/Toast";
import ToggleSwitch from "components/ToggleSwitch";
import Modal from "components/Modal";
import FacebookAdsOfflineConversionWidget from "components/facebook/OfflineConversionWidget";
import FacebookAdsOfflineConversionCrm from "components/facebook/OfflineConversionCrm";
import Loading from "components/gads/Loading";
import LoadingContainer from "components/LoadingContainer";
import styled from "styled-components";
import facebookActions from "modules/facebook/actions";
import facebookSelectors from "modules/facebook/selectors";
import { useTranslation } from "react-i18next";
import { TruckPlane } from "styled-icons/fa-solid";
import mainSelectors from "modules/main/selectors";

const OfflineConversion = () => {
  const rmpToken = sessionStorage.getItem("rmp_token");
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState("r-widget");
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showFailedToast, setShowFailedToast] = useState(false);
  const [modalActiveConversionDisplay, setModalActiveConversionDisplay] = useState("hide");
  const [modalInactiveConversionDisplay, setModalInactiveConversionDisplay] = useState("hide");
  const [conversionStatusLoading, setConversionStatusLoading] = useState(false);
  const facebookSettingsResult = useSelector((state) => facebookSelectors.getFacebookSettingsResult(state));
  const menu = "facebook_ads_web_conversion";
  const RcrmAppAvailableResult = useSelector((state) => mainSelectors.checkAppAvailableResult(state));

  //eslint-disable-next-line no-unused-vars
  const [t, i18n] = useTranslation();

  const RenderContent = useMemo(() => {
    if (currentTab === "r-widget") {
      return <FacebookAdsOfflineConversionWidget />;
    } else if (currentTab === "r-crm") {
      return <FacebookAdsOfflineConversionCrm />;
    }
  }, [currentTab]);

  const changeCurrentTab = (tab) => {
    setCurrentTab(tab);
  };

  useEffect(() => {
    getFacebookSettings();
  }, []);

  const getFacebookSettings = async () => {
    await dispatch(
      facebookActions.getFacebookSettings({
        token: rmpToken,
      }),
    );

    const conversionLastSent = await dispatch(
      facebookActions.getFacebookConversionLastSent({
        token: rmpToken,
      }),
    );
    if (menu === sessionStorage.getItem("selectedMenu")) {
      setLoading(false);
    }
  };

  const handleActiveConversionCloseModal = () => {
    setModalActiveConversionDisplay("hide");
  };

  const handleInactiveConversionCloseModal = () => {
    setModalInactiveConversionDisplay("hide");
  };

  const handleClickActiveConversion = (event) => {
    event.preventDefault();

    if (facebookSettingsResult.data[0].web_conversion_status === "active") {
      setModalInactiveConversionDisplay("show");
    } else {
      setModalActiveConversionDisplay("show");
    }
  };

  const handleActiveConversion = async () => {
    setConversionStatusLoading(true);
    const response = await dispatch(
      facebookActions.setFacebookConversionStatus({
        token: rmpToken,
        fbConversionStatus: "on",
        conversionType: "web",
      }),
    );

    if (response.result === 0) {
      setShowSuccessToast(true);
    } else {
      setShowFailedToast(true);
    }

    facebookSettingsResult.data[0].web_conversion_status = "active";

    setModalActiveConversionDisplay("hide");
    setConversionStatusLoading(false);
  };

  const handleInactiveConversion = async () => {
    setConversionStatusLoading(true);
    const response = await dispatch(
      facebookActions.setFacebookConversionStatus({
        token: rmpToken,
        fbConversionStatus: "off",
        conversionType: "web",
      }),
    );

    if (response.result === 0) {
      setShowSuccessToast(true);
    } else {
      setShowFailedToast(true);
    }

    facebookSettingsResult.data[0].web_conversion_status = "inactive";

    setModalInactiveConversionDisplay("hide");
    setConversionStatusLoading(false);
  };

  const handleChangeActiveLeadConversion = () => {
    console.log("handleChangeActiveLeadConversion");
    // Do nothing
  };

  const successToastCallback = (value) => {
    setShowSuccessToast(value);
  };

  const failedToastCallback = (value) => {
    setShowFailedToast(value);
  };

  const modalActiveConversionContent = (
    <ModalContentContainer>
      <div className="info-icon">&#9432;</div>
      <div>{t("__fbads_web_conversion_active_confirm")}</div>
    </ModalContentContainer>
  );

  const modalActiveConversionFooter = (
    <ModalButtonContainer>
      <Button type="secondary" onClick={handleActiveConversionCloseModal}>
        {t("__ads_offline_conversion_cancel_button")}
      </Button>
      <Button type="loading" onClick={handleActiveConversion} loading={conversionStatusLoading}>
        {t("__ads_offline_conversion_confirm_button")}
      </Button>
    </ModalButtonContainer>
  );
  const modalInactiveConversionContent = (
    <ModalContentContainer>
      <div className="info-icon">&#9432;</div>
      <div>{t("__fbads_web_conversion_inactive_confirm")}</div>
    </ModalContentContainer>
  );
  const modalInactiveConversionFooter = (
    <ModalButtonContainer>
      <Button type="secondary" onClick={handleInactiveConversionCloseModal}>
        {t("__ads_offline_conversion_cancel_button")}
      </Button>
      <Button type="loading" onClick={handleInactiveConversion} loading={conversionStatusLoading}>
        {t("__ads_offline_conversion_confirm_button")}
      </Button>
    </ModalButtonContainer>
  );

  return (
    <>
      <RelativeDiv>
        <SetupTextHeader>{t("__fbads_web_conversion_title")}</SetupTextHeader>
        <TextHeaderDescription>{t("__fbads_web_conversion_description")}</TextHeaderDescription>
        <ConversionSettingsContainer>
          <FlexSpaceBetween>
            <ConversionStatusLabel>{t("__fbads_conversion_sync")}</ConversionStatusLabel>
            <div>
              <ToggleSwitch
                value="on"
                checked={facebookSettingsResult.data[0].web_conversion_status === "active"}
                onClick={(event) => handleClickActiveConversion(event)}
                onChange={handleChangeActiveLeadConversion}
              ></ToggleSwitch>
            </div>
          </FlexSpaceBetween>
          <FlexCenter>
            <div>
              <ConnectionImage
                src={
                  facebookSettingsResult.data[0].web_conversion_status === "active"
                    ? "/images/meta_connected.png"
                    : "/images/meta_disconnected.png"
                }
              />
            </div>
          </FlexCenter>
        </ConversionSettingsContainer>

        {facebookSettingsResult.data[0].web_conversion_status === "active" ? (
          loading === true ? (
            <Loading />
          ) : (
            <div>
              <Tabs>
                <TabPanel
                  className={currentTab === "r-widget" ? "selected" : ""}
                  onClick={() => {
                    changeCurrentTab("r-widget");
                  }}
                >
                  R-Widget
                </TabPanel>
                {RcrmAppAvailableResult &&
                RcrmAppAvailableResult.result === 0 &&
                RcrmAppAvailableResult.data &&
                RcrmAppAvailableResult.data.foundApp ? (
                  <TabPanel
                    className={currentTab === "r-crm" ? "selected" : ""}
                    onClick={() => {
                      changeCurrentTab("r-crm");
                    }}
                  >
                    R-CRM
                  </TabPanel>
                ) : (
                  ""
                )}
                <TabDummy></TabDummy>
              </Tabs>

              <div>{RenderContent}</div>
            </div>
          )
        ) : (
          ""
        )}

        <Toast
          show={showSuccessToast}
          message={t("__ads_offline_conversion_save_success")}
          bgColor="#4CAF50"
          callback={successToastCallback}
        ></Toast>
        <Toast
          show={showFailedToast}
          message={t("__ads_offline_conversion_save_failed")}
          bgColor="#F44336"
          callback={failedToastCallback}
        ></Toast>
        <Modal
          state={modalActiveConversionDisplay}
          content={modalActiveConversionContent}
          footer={modalActiveConversionFooter}
        />
        <Modal
          state={modalInactiveConversionDisplay}
          content={modalInactiveConversionContent}
          footer={modalInactiveConversionFooter}
        />
      </RelativeDiv>
    </>
  );
};

export default OfflineConversion;

const SetupTextHeader = styled(TextHeader)`
  font-size: 18px;
  margin-bottom: 10px;
  font-weight: bold;
`;

const TextHeaderDescription = styled.div`
  font-size: 14px;
  color: #999;
  margin-bottom: 10px;
`;

const Tabs = styled.div`
  margin-top: 10px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  padding-left: 0px;
  border-bottom: 1px solid #eee;

  @media (max-width: 768px) {
    width: 90%;
  }
`;

const TabPanel = styled.div`
  min-width: 100px;
  max-width: 50%;
  padding: 10px;
  list-style: none;
  text-align: center;
  cursor: pointer;

  &.selected {
    border-bottom: 2px solid #0088e3;
  }
`;

const TabDummy = styled.div`
  flex-grow: 1;
`;

const StyledLabel = styled.label`
  cursor: pointer;
  vertical-align: middle;
`;

const StyledCheckbox = styled.input`
  cursor: pointer;
  vertical-align: middle;
`;

const ModalContentContainer = styled.div`
  text-align: center;
  margin-bottom: 20px;
  color: #575757;
  font-weight: bold;

  .info-icon {
    font-size: 78px;
    color: #f8bb86;
    margin-bottom: 20px;
    font-weight: normal;
  }
`;

const ModalButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  padding-bottom: 16px;

  > Button {
    min-width: 100px;
    margin-bottom: 0px;
  }

  > Button:first-child {
    margin-right: 10px;
  }
`;

const ConversionSettingsContainer = styled.div`
  border-radius: 8px;
  border: 1px solid #eee;
  padding: 20px;
`;

const FlexSpaceBetween = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FlexCenter = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ConnectionImage = styled.img`
  margin-top: 20px;
  width: 100%;
  max-width: 300px;
`;

const ConversionStatusLabel = styled.div`
  color: #999;
  font-size: 0.9rem;
`;

const RelativeDiv = styled.div`
  position: relative;
`;
